import { useState } from "react";
import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { EmptyState, PageHeader } from "@/components/ui";
import { RequiresConnection } from "@/components/requires-connection";
import { SignupWizard } from "@/components/signup-wizard/wizard-shell";
import { CompanyInviteDoor } from "@/components/get-started-doors/company-invite-door";
import { PendingInvites } from "@/components/company/home/pending-invites";
import { RosterHealth } from "@/components/company/home/roster-health";
import { UtilityStatuses } from "@/components/company/home/utility-statuses";
import { RejectedAccessRequests } from "@/components/company/home/rejected-access-requests";
import { RejectedSubmissions } from "@/components/company/home/rejected-submissions";
import { RejectedChangeRequests } from "@/components/company/home/rejected-change-requests";
import { SuspensionBanner } from "@/components/utilities/suspension-banner";
import { useIsOnline } from "@/hooks/use-is-online";
import { usePersona } from "@/hooks/use-persona";
import { useActiveMembership } from "@/hooks/use-company-context";
import { isCompanyAdminRole } from "@/hooks/use-company-role";
import { hasLiveTesterArm } from "@/lib/resolve-persona";

export const Route = createFileRoute("/_company/")({
  component: HomePage,
});

const mfaCtaClass =
  "inline-flex min-h-11 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-hover";

/**
 * The portal's landing surface on `/` — the ADMIN home, the create-or-join
 * doors, and the quiet states, composed BY CAPABILITY rather than by persona.
 * The tester's own surfaces live on the Work page (`/my-work`): its banners,
 * KPI numbers, queue, archive and change requests all absorbed there, so what
 * remains here is the company-admin workspace and the ways in.
 *
 * The company sections ride an `admin` membership, and the role comes off the
 * persona resolution rather than a second membership read.
 *
 * The suspension banner sits above all of it and outside the blocks. It
 * belongs to neither persona: a water system that has paused or ended the
 * company's authorization stops the admin's business and empties the tester's
 * queue, and it self-gates on the live membership so it mounts unconditionally.
 *
 * `/` resolves FOUR ways, first match wins: MFA/quiet standings keep their
 * screens; a grant with NO membership keeps the create-or-join doors; an
 * admin membership (admin-only or dual) keeps this admin home; a TESTER-ONLY
 * member redirects to `/my-work` — the Work page is their whole portal, and
 * no nav entry of theirs points here.
 *
 * A session holding a grant and NO membership gets the create-or-join slot
 * instead: the signup wizard (standing where the registration door did) and
 * the company-invite door, which renders nothing when there is no invitation
 * to accept. The wizard's register verb invalidates both persona probes — so
 * founding a company from here re-resolves this page into the admin one with
 * no reload, and an ADMIN whose company is still incomplete keeps the wizard
 * right here, resumed from the server's completeness truth, until the profile
 * is whole.
 *
 * ONE CLOCK, read once and handed to every section. Independent `new Date()`
 * calls can straddle midnight and put a due-soon count, a month-to-date window
 * and a 30-day expiry window into different days.
 *
 * OFFLINE IT SAYS SO. Every datum here is a live round trip — the roster, the
 * company's water systems, the decisions waiting on review — and none of it is
 * bundle-backed. With no connection the sections would sit on unresolvable
 * queries forever, which reads as an empty workspace. The tester's queued work
 * lives on the Work page, which the sidebar keeps one tap away.
 */
function HomePage() {
  const { t } = useTranslation(["field-work", "company"]);
  const isOnline = useIsOnline();
  const { resolution } = usePersona();
  // The membership ROW (completeness block included) off the same settled
  // bootstrap read the persona probe classified from — it decides whether the
  // admin standing here is still mid-signup.
  const activeMembership = useActiveMembership();
  // One read, at mount. A clock that ticked would redraw every window on the
  // page and refetch four counts for no new information.
  const [now] = useState(() => new Date());

  const membership = resolution.kind === "admitted" ? resolution.membership : null;
  const testerArm = hasLiveTesterArm(resolution);
  // The membership the company sections are drawn for, or null — a `tester`
  // membership reads this page as a tester and sees none of them.
  const adminMembership =
    membership !== null && isCompanyAdminRole(membership.role) ? membership : null;
  const createOrJoin = testerArm && membership === null;
  // A company whose profile is still missing elements of the signup mandatory
  // set: the wizard RESUMES here, from server state, because this page is
  // where founding a company lands the founder (the register verb flips the
  // persona into this shell on its very next probe).
  const companyIncomplete =
    adminMembership !== null && (activeMembership?.completeness.missing.length ?? 0) > 0;
  // Reachable twice over: a `tester` membership whose every grant was revoked,
  // and an `mfaRequired` session standing here rather than on enrolment. Both
  // are admitted personas with nothing on this page, and a blank one would read
  // as a broken portal rather than as the standing it is.
  const nothingToShow = !testerArm && adminMembership === null;
  const mfaOutstanding = resolution.kind === "mfaRequired";
  // Case 4 of the four-case `/` resolution, the ONLY one that redirects: a
  // live tester arm plus a tester-role membership has no admin workspace, no
  // doors and no quiet state to keep `/` for — the Work page is their whole
  // portal. Every other standing KEEPS `/` (the doors, the MFA/quiet screens,
  // the admin home), and the redirect never fires on unresolved data: it reads
  // only an `admitted` resolution's own membership and role.
  const testerOnlyMember =
    resolution.kind === "admitted" &&
    testerArm &&
    membership !== null &&
    !isCompanyAdminRole(membership.role);

  if (!isOnline) {
    return (
      <RequiresConnection
        showTesterCta={false}
        title={t("home.offline.title")}
        body={t("home.offline.body")}
      />
    );
  }

  // In-component by necessity, mirroring the kits/new guard: `beforeLoad`
  // runs outside React and cannot read the membership, and an unresolved
  // resolution must never bounce a tester off the page they landed on.
  if (testerOnlyMember) return <Navigate to="/my-work" replace />;

  return (
    <div className="space-y-6 p-4 sm:p-8 print:p-0">
      {/* Self-gating on the live membership, above every section and outside
          both persona blocks: a suspension stops the company's work, so it is
          the first thing the page says to the admin who must act on it and to
          the tester whose queue it empties. */}
      <div className="print:hidden">
        <SuspensionBanner />
      </div>

      {companyIncomplete && (
        <div className="print:hidden">
          <SignupWizard membership={activeMembership} />
        </div>
      )}

      {adminMembership !== null && !companyIncomplete && (
        <div className="space-y-6 print:hidden">
          <PageHeader title={t("company:home.title")} subtitle={t("company:home.subtitle")} />
          <RosterHealth companyId={adminMembership.companyId} now={now} />
          <UtilityStatuses companyId={adminMembership.companyId} />
          <RejectedAccessRequests companyId={adminMembership.companyId} />
          <RejectedSubmissions companyId={adminMembership.companyId} now={now} />
          <RejectedChangeRequests companyId={adminMembership.companyId} now={now} />
          <PendingInvites companyId={adminMembership.companyId} />
        </div>
      )}

      {createOrJoin && (
        <div className="space-y-4 print:hidden">
          <SignupWizard membership={null} />
          <CompanyInviteDoor />
        </div>
      )}

      {nothingToShow &&
        (mfaOutstanding ? (
          <div className="space-y-4 print:hidden">
            <EmptyState
              title={t("company:home.quiet.mfaTitle")}
              description={t("company:home.quiet.mfaDescription")}
            />
            <div className="flex justify-center">
              <Link to="/settings" className={mfaCtaClass}>
                {t("company:tester.mfaCta")}
              </Link>
            </div>
          </div>
        ) : (
          <div className="print:hidden">
            <EmptyState
              title={t("company:home.quiet.title")}
              description={t("company:home.quiet.description")}
            />
          </div>
        ))}
    </div>
  );
}
